/**
 * Taxonomy page — per docs/specs/taxonomy.md
 *
 * Default stages, workflows, standard tag library by category. Edits to
 * any default flow through the migration modal (§7) with scope selection
 * and audit-logged rollback in the migration history card.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v5
 */
import { useState } from 'react';
import { useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { GripVertical, Pencil } from 'lucide-react';
import { Chip } from '../components/chip';
import { RoleButton } from '../components/role-gate';
import { useToast } from '../components/toast';
import {
    TaxonomyMigrationModal,
    type TaxonomyMigrationApplyPayload,
    type TaxonomyMigrationTarget
} from './taxonomy-migration-modal';
import { TaxonomyTagEditorModal } from './taxonomy-tag-editor-modal';
import { TaxonomyCategoryEditorModal } from './taxonomy-category-editor-modal';
import {
    reorderTaxonomyTags,
    useStages,
    useTagCategories,
    useTags,
    useTaxonomyKpis,
    useTaxonomyMigrations,
    useWorkflows
} from '../../lib/api';
import { apiPost, getHttpErrorMessage } from '../../lib/http';
import type {
    TaxonomyCategory,
    TaxonomyCategorySlug,
    TaxonomyMigration,
    TaxonomyTag
} from '../../lib/types';

type TagEditorState =
    | { open: false }
    | { open: true; mode: 'create'; defaultCategory: TaxonomyCategorySlug }
    | { open: true; mode: 'edit'; tag: TaxonomyTag };

type CategoryEditorState =
    | { open: false }
    | { open: true; mode: 'create' }
    | { open: true; mode: 'edit'; category: TaxonomyCategory };

/** Fuzzy-match slug to one of the categories returned by the /categories API. */
function findCategoryBySlug(
    categories: TaxonomyCategory[] | undefined,
    slug: TaxonomyCategorySlug
): TaxonomyCategory | null {
    if (!categories || categories.length === 0) return null;
    const lower = slug.toLowerCase();
    const exact = categories.find((c) => c.name.toLowerCase() === lower);
    if (exact) return exact;
    const partial = categories.find((c) => {
        const catLower = c.name.toLowerCase();
        return catLower.includes(lower) || lower.includes(catLower);
    });
    return partial ?? null;
}

const scopeTone = (s: TaxonomyMigration['scope']) =>
    s === 'new_users_only' ? 'muted' : s === 'retro_untouched' ? 'info' : 'warning';

const scopeLabel = (s: TaxonomyMigration['scope']) =>
    s === 'new_users_only'
        ? 'new users'
        : s === 'retro_untouched'
          ? 'retro · untouched'
          : 'retro · all';

const CATEGORY_ORDER: TaxonomyTag['category'][] = [
    'genre',
    'mood',
    'instrumentation',
    'purpose',
    'status',
    'format'
];

export function TaxonomyPage() {
    const kpis = useTaxonomyKpis();
    const stages = useStages();
    const workflows = useWorkflows();
    const tags = useTags();
    const categories = useTagCategories();
    const migrations = useTaxonomyMigrations();
    const qc = useQueryClient();
    const [migrationTarget, setMigrationTarget] = useState<TaxonomyMigrationTarget | null>(null);
    const [tagEditor, setTagEditor] = useState<TagEditorState>({ open: false });
    const [categoryEditor, setCategoryEditor] = useState<CategoryEditorState>({ open: false });
    // Drag-drop reorder state (TAX-FN-010). Tracks which tag is being dragged and from which category,
    // plus the id of the chip currently hovered so we can render a drop indicator.
    const [drag, setDrag] = useState<{ tagId: string; fromCategory: TaxonomyCategorySlug } | null>(
        null
    );
    const [dragOverId, setDragOverId] = useState<string | null>(null);
    const { toast } = useToast();
    const historyRef = useRef<HTMLElement | null>(null);

    const tagsByCategory = (tags.data ?? []).reduce<Record<string, TaxonomyTag[]>>((acc, t) => {
        (acc[t.category] ??= []).push(t);
        return acc;
    }, {});

    /**
     * Commit a drag-drop reorder for tags within a single category.
     * Optimistic: rewrites the tags-cache array so the new order shows instantly,
     * fires the reorder endpoint, and reverts on failure.
     */
    const handleReorderDrop = async (
        category: TaxonomyCategorySlug,
        draggedTagId: string,
        overTagId: string
    ) => {
        if (draggedTagId === overTagId) return;
        const currentTags = tags.data ?? [];
        const inCategory = currentTags.filter((t) => t.category === category);
        const others = currentTags.filter((t) => t.category !== category);
        const fromIdx = inCategory.findIndex((t) => t.id === draggedTagId);
        const toIdx = inCategory.findIndex((t) => t.id === overTagId);
        if (fromIdx === -1 || toIdx === -1) return;

        const reordered = [...inCategory];
        const [moved] = reordered.splice(fromIdx, 1);
        reordered.splice(toIdx, 0, moved);

        // Optimistic cache update
        const newFull = [...others, ...reordered];
        qc.setQueryData(['admin', 'taxonomy', 'tags'], newFull);

        try {
            await reorderTaxonomyTags(reordered.map((t) => t.id));
            qc.invalidateQueries({ queryKey: ['admin', 'taxonomy', 'tags'] });
        } catch (err) {
            qc.setQueryData(['admin', 'taxonomy', 'tags'], currentTags);
            toast(`Reorder failed: ${getHttpErrorMessage(err, 'try again')}`);
        }
    };

    const handleOpenHistory = () => {
        historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        toast('Scrolled to migration history');
    };

    const handleApplyMigration = async (payload: TaxonomyMigrationApplyPayload) => {
        try {
            await apiPost('/api/admin/taxonomy/migrations', payload);
            await qc.invalidateQueries({ queryKey: ['admin', 'taxonomy', 'migrations'] });
            toast('Migration request accepted', {
                detail: `Scope ${payload.scope}`
            });
        } catch (error) {
            toast('Migration request failed', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Migration request blocked')
            });
            throw error;
        }
    };

    const handleRollbackMigration = async (migrationId: string, rollbackAvailable: boolean) => {
        if (!rollbackAvailable) return;
        const reason = window.prompt('Rollback reason (required):')?.trim();
        if (!reason) return;

        try {
            await apiPost(`/api/admin/taxonomy/migrations/${migrationId}/rollback`, { reason });
            await qc.invalidateQueries({ queryKey: ['admin', 'taxonomy', 'migrations'] });
            toast('Rollback request submitted', {
                detail: `Migration ${migrationId}`
            });
        } catch (error) {
            toast('Rollback request failed', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Rollback request blocked')
            });
        }
    };

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Taxonomy</h1>
                    <p>Default stages, workflows, and the shipped tag library.</p>
                </div>
                <div className="page-actions">
                    <button className="btn btn-ghost" onClick={handleOpenHistory}>
                        Migration history
                    </button>
                </div>
            </header>

            <section className="alert-bar">
                <div className="alert" data-severity="info">
                    <span className="msg">
                        Taxonomy changes are validated on a worker-side dry-run route, while the
                        physical default-library editor is still unavailable in this repo. Use the
                        migration preview, history, and rollback receipt flow for now.
                    </span>
                    <span className="meta">partial</span>
                </div>
            </section>

            <section className="kpi-grid kpi-grid-4" aria-label="Taxonomy metrics">
                <KpiMini label="Default stages" value={kpis.data?.stages.toString() ?? '—'} />
                <KpiMini
                    label="Workflows"
                    value={
                        kpis.data
                            ? `${kpis.data.workflows_system} system · ${kpis.data.workflows_optional} optional`
                            : '—'
                    }
                />
                <KpiMini label="Standard tags" value={kpis.data?.standard_tags.toString() ?? '—'} />
                <KpiMini
                    label="Custom user tags"
                    value={kpis.data?.custom_tags.toLocaleString() ?? '—'}
                />
            </section>

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Stages</h2>
                        <button
                            className="btn btn-sm btn-ghost"
                            onClick={() =>
                                setMigrationTarget({
                                    kind: 'stage',
                                    name: 'New stage',
                                    change: 'Add default stage'
                                })
                            }
                        >
                            + Add stage
                        </button>
                    </header>
                    <div className="card-body tight">
                        {stages.data?.map((s) => (
                            <div
                                key={s.id}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '20px 16px 1fr auto auto',
                                    gap: 10,
                                    alignItems: 'center',
                                    padding: '10px 16px',
                                    borderBottom: '1px solid var(--border)',
                                    fontSize: 12
                                }}
                            >
                                <GripVertical size={14} color="var(--text-dim)" />
                                <span
                                    style={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: 3,
                                        background: `var(${s.color_var})`
                                    }}
                                />
                                <span style={{ color: 'var(--text-primary)' }}>{s.name}</span>
                                <span className="text-muted mono">
                                    {s.track_count.toLocaleString()}
                                </span>
                                <button
                                    className="btn btn-sm btn-ghost"
                                    onClick={() =>
                                        setMigrationTarget({
                                            kind: 'stage',
                                            name: s.name,
                                            change: `Edit stage "${s.name}"`
                                        })
                                    }
                                    aria-label={`Edit stage ${s.name}`}
                                >
                                    <Pencil size={12} />
                                </button>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Workflows</h2>
                        <button
                            className="btn btn-sm btn-ghost"
                            onClick={() =>
                                setMigrationTarget({
                                    kind: 'workflow',
                                    name: 'New workflow',
                                    change: 'Add default workflow'
                                })
                            }
                        >
                            + Add workflow
                        </button>
                    </header>
                    <div className="card-body tight">
                        {workflows.data?.map((w) => (
                            <div
                                key={w.id}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 1fr auto',
                                    gap: 10,
                                    alignItems: 'center',
                                    padding: '12px 16px',
                                    borderBottom: '1px solid var(--border)',
                                    fontSize: 12
                                }}
                            >
                                <Chip tone={w.is_system ? 'info' : 'muted'}>
                                    {w.is_system ? 'system' : 'optional'}
                                </Chip>
                                <div>
                                    <div style={{ color: 'var(--text-primary)' }}>{w.name}</div>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                        {w.description}
                                    </div>
                                </div>
                                <button
                                    className="btn btn-sm btn-ghost"
                                    onClick={() =>
                                        setMigrationTarget({
                                            kind: 'workflow',
                                            name: w.name,
                                            change: `Edit workflow "${w.name}"`
                                        })
                                    }
                                    aria-label={`Edit workflow ${w.name}`}
                                >
                                    <Pencil size={12} />
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            <section className="card">
                <header
                    className="card-header"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <h2 className="card-title">Standard tag library</h2>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-sm btn-ghost"
                        onClick={() => setCategoryEditor({ open: true, mode: 'create' })}
                        title="Add a new default tag category (e.g. Tempo, Energy, Vocals)"
                    >
                        + Category
                    </RoleButton>
                </header>
                <div className="card-body">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {CATEGORY_ORDER.map((cat) => {
                            const matched = findCategoryBySlug(categories.data, cat);
                            return (
                                <div key={cat}>
                                    <div
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 600,
                                            color: 'var(--text-muted)',
                                            letterSpacing: '0.08em',
                                            textTransform: 'uppercase',
                                            marginBottom: 6,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8
                                        }}
                                    >
                                        <span>{cat}</span>
                                        {matched && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-ghost"
                                                onClick={() =>
                                                    setCategoryEditor({
                                                        open: true,
                                                        mode: 'edit',
                                                        category: matched
                                                    })
                                                }
                                                title={`Edit "${matched.name}" category`}
                                                style={{
                                                    padding: '2px 6px',
                                                    fontSize: 10,
                                                    textTransform: 'none',
                                                    letterSpacing: 0
                                                }}
                                            >
                                                <Pencil size={10} />
                                            </button>
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 6
                                        }}
                                    >
                                        {(tagsByCategory[cat] ?? []).map((t) => (
                                            <button
                                                key={t.id}
                                                type="button"
                                                className="chip"
                                                draggable
                                                onDragStart={(e) => {
                                                    setDrag({ tagId: t.id, fromCategory: cat });
                                                    e.dataTransfer.effectAllowed = 'move';
                                                    e.dataTransfer.setData('text/plain', t.id);
                                                }}
                                                onDragEnd={() => {
                                                    setDrag(null);
                                                    setDragOverId(null);
                                                }}
                                                onDragOver={(e) => {
                                                    if (
                                                        drag &&
                                                        drag.fromCategory === cat &&
                                                        drag.tagId !== t.id
                                                    ) {
                                                        e.preventDefault();
                                                        e.dataTransfer.dropEffect = 'move';
                                                        setDragOverId(t.id);
                                                    }
                                                }}
                                                onDragLeave={() => {
                                                    if (dragOverId === t.id) setDragOverId(null);
                                                }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    if (
                                                        drag &&
                                                        drag.fromCategory === cat &&
                                                        drag.tagId !== t.id
                                                    ) {
                                                        void handleReorderDrop(
                                                            cat,
                                                            drag.tagId,
                                                            t.id
                                                        );
                                                    }
                                                    setDrag(null);
                                                    setDragOverId(null);
                                                }}
                                                onClick={() =>
                                                    setTagEditor({
                                                        open: true,
                                                        mode: 'edit',
                                                        tag: t
                                                    })
                                                }
                                                style={{
                                                    background: `color-mix(in oklab, var(${t.color_var}) 12%, transparent)`,
                                                    color: `var(${t.color_var})`,
                                                    height: 24,
                                                    padding: '0 10px',
                                                    border:
                                                        dragOverId === t.id
                                                            ? `1px dashed var(${t.color_var})`
                                                            : 'none',
                                                    opacity: drag?.tagId === t.id ? 0.4 : 1,
                                                    cursor:
                                                        drag?.tagId === t.id ? 'grabbing' : 'grab'
                                                }}
                                                title={`Edit ${t.name} (drag to reorder)`}
                                            >
                                                {t.name}
                                            </button>
                                        ))}
                                        <button
                                            className="btn btn-sm btn-ghost"
                                            onClick={() =>
                                                setTagEditor({
                                                    open: true,
                                                    mode: 'create',
                                                    defaultCategory: cat
                                                })
                                            }
                                            title={`Add a new default ${cat} tag`}
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="card" ref={historyRef}>
                <header className="card-header">
                    <h2 className="card-title">Migration history</h2>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        Every default change is logged with scope and a per-user rollback token.
                    </span>
                </header>
                <div className="table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>When</th>
                                <th>Actor</th>
                                <th>Change</th>
                                <th>Scope</th>
                                <th className="numeric">Affected users</th>
                                <th>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            {migrations.data && migrations.data.length > 0 ? (
                                migrations.data.map((m) => (
                                    <tr key={m.id}>
                                        <td className="mono text-muted">{m.ts}</td>
                                        <td className="text-muted">{m.actor}</td>
                                        <td>{m.change}</td>
                                        <td>
                                            <Chip tone={scopeTone(m.scope)}>
                                                {scopeLabel(m.scope)}
                                            </Chip>
                                        </td>
                                        <td className="numeric mono">
                                            {m.affected_users.toLocaleString()}
                                        </td>
                                        <td>
                                            <RoleButton
                                                scope="owner_only"
                                                className="btn btn-sm btn-ghost"
                                                disabled={!m.rollback_available}
                                                onClick={() =>
                                                    handleRollbackMigration(
                                                        m.id,
                                                        m.rollback_available
                                                    )
                                                }
                                                title={
                                                    m.rollback_available
                                                        ? 'Record rollback receipt'
                                                        : m.status === 'rolled_back'
                                                          ? 'Already rolled back'
                                                          : 'Rollback token unavailable'
                                                }
                                            >
                                                {m.status === 'rolled_back'
                                                    ? 'Rolled back'
                                                    : 'Rollback'}
                                            </RoleButton>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6} className="empty-state">
                                        No persisted taxonomy migration history yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <TaxonomyMigrationModal
                target={migrationTarget}
                onClose={() => setMigrationTarget(null)}
                onApply={handleApplyMigration}
            />

            <TaxonomyTagEditorModal
                open={tagEditor.open}
                mode={tagEditor.open ? tagEditor.mode : 'create'}
                tag={tagEditor.open && tagEditor.mode === 'edit' ? tagEditor.tag : null}
                defaultCategory={
                    tagEditor.open && tagEditor.mode === 'create' ? tagEditor.defaultCategory : null
                }
                onClose={() => setTagEditor({ open: false })}
                onSaved={() => {
                    qc.invalidateQueries({ queryKey: ['admin', 'taxonomy', 'tags'] });
                    qc.invalidateQueries({ queryKey: ['admin', 'taxonomy', 'kpis'] });
                    toast('Tag saved');
                }}
            />

            <TaxonomyCategoryEditorModal
                open={categoryEditor.open}
                mode={categoryEditor.open ? categoryEditor.mode : 'create'}
                category={
                    categoryEditor.open && categoryEditor.mode === 'edit'
                        ? categoryEditor.category
                        : null
                }
                onClose={() => setCategoryEditor({ open: false })}
                onSaved={() => {
                    qc.invalidateQueries({ queryKey: ['admin', 'taxonomy', 'categories'] });
                    qc.invalidateQueries({ queryKey: ['admin', 'taxonomy', 'tags'] });
                    qc.invalidateQueries({ queryKey: ['admin', 'taxonomy', 'kpis'] });
                    toast('Category saved');
                }}
            />
        </div>
    );
}

function KpiMini({ label, value }: { label: string; value: string }) {
    return (
        <div className="kpi-tile">
            <span className="label">{label}</span>
            <span className="value">{value}</span>
        </div>
    );
}
